import { Fragment, useState } from "react";
import { addRider, isRowWatched, lastNameOf } from "../lib/watchlist";
import { useWatchList } from "../lib/useWatchList";
import type { RaceDayResults } from "../lib/types";

interface Props {
  results: RaceDayResults;
  watchedRiders: string[];
  /** Optional rider param from the URL — exact-ish substring to highlight extra. */
  spotlight?: string;
}

export default function ResultsTable({ results, watchedRiders, spotlight }: Props) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const liveWatch = useWatchList();
  const allWatched = spotlight
    ? [...watchedRiders, spotlight]
    : watchedRiders;

  if (results.rows.length === 0) {
    return (
      <div className="rounded-md border border-pit-border pit-panel p-6 text-center text-sm text-pit-muted font-mono">
        No results posted yet.
      </div>
    );
  }

  // Choose a clean column set. We avoid the empty leading/trailing decoration
  // columns RaceDay leaves in for sort indicators and row-expand buttons.
  const visibleHeaders = results.columns.filter(
    (c) => c.length > 0 && c !== "category_name" && c !== "Details",
  );
  const renderHeader = (h: string) => (h === "Total Time" ? "Time" : h === "Number of Laps" ? "Laps" : h);

  return (
    <div className="rounded-md border border-pit-border pit-panel overflow-x-auto">
      <table className="table-data">
        <thead>
          <tr>
            {visibleHeaders.map((h) => (
              <th key={h}>{renderHeader(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {results.rows.map((row, i) => {
            const watched = isRowWatched(row.name, allWatched);
            const rowKey = `${row.bib}:${i}`;
            const isOpen = openKey === rowKey;
            const surname = lastNameOf(row.name);
            const alreadyWatching = surname
              ? liveWatch.riders.some((r) => r.toLowerCase() === surname.toLowerCase())
              : false;
            const laps = row.lapBreakdown;
            return (
              <Fragment key={rowKey}>
                <tr
                  className={`cursor-pointer ${watched ? "watched" : ""}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenKey(isOpen ? null : rowKey)}
                >
                  {visibleHeaders.map((h) => (
                    <td key={h} className={cellClass(h)}>
                      {h === "Name" ? (
                        <span className="row-name">{row.raw[h] ?? ""}</span>
                      ) : (
                        row.raw[h] ?? ""
                      )}
                    </td>
                  ))}
                </tr>
                {isOpen && (
                  <tr className={watched ? "watched" : undefined}>
                    <td colSpan={visibleHeaders.length} className="bg-pit-bg/40">
                      <div className="space-y-3 py-2">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="flex items-baseline gap-2 min-w-0">
                            <span className="text-lg sm:text-xl font-bold break-words">
                              {row.name}
                            </span>
                            {row.bib && (
                              <span className="text-pit-muted text-sm font-mono">
                                #{row.bib}
                              </span>
                            )}
                          </div>
                          <button
                            className={`btn btn-primary ${alreadyWatching ? "opacity-60 pointer-events-none" : ""}`}
                            disabled={alreadyWatching || !surname}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (surname && !alreadyWatching) addRider(surname);
                            }}
                          >
                            {alreadyWatching ? "★ watching" : "★ watch this rider"}
                          </button>
                        </div>
                        {laps && laps.length > 0 && (
                          <div className="overflow-x-auto">
                            <table className="text-base font-mono tabular-nums w-full">
                              <thead>
                                <tr>
                                  {Object.keys(laps[0]).map((k) => (
                                    <th
                                      key={k}
                                      className="text-left px-3 py-1.5 text-[11px] uppercase tracking-wider text-pit-muted border-b border-pit-border"
                                    >
                                      {k}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {laps.map((lap, li) => (
                                  <tr key={li}>
                                    {Object.keys(laps[0]).map((k) => (
                                      <td key={k} className="px-3 py-1.5 border-b border-pit-border/60">
                                        {lap[k] ?? ""}
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function cellClass(header: string): string | undefined {
  if (header === "Place") return "text-pit-gold font-bold w-12";
  if (header === "Bib") return "text-pit-muted w-16";
  if (header === "Total Time" || header === "Duration" || header === "Times") return "tabular-nums";
  if (header === "Gap") return "tabular-nums text-pit-muted";
  if (header === "Number of Laps" || header === "Laps") return "tabular-nums w-16 text-center";
  return undefined;
}
